<template>
  <Teleport
    to="body"
  >
    <ConsumerHeader
      v-if="headerVisible"
      ref="consumerHeaderRef"
      :file-info="fileInfo"
      @close="handleClose"
      @operate="handleOperate"
      @height-update="handleHeaderHeightUpdate"
    />
  </Teleport>
</template>

<script
  lang="ts"
  setup
>
import {
  inject,
  onBeforeMount,
  onBeforeUnmount,
  reactive,
  ref,
  useRoute,
  watch,
} from '#imports'
import { InjectionKey } from '@/types/enum'
import { authByLocalStorage } from '@/utils'
import { injectJsBridge } from '@/utils/webBridge'
import { createContentConsumer } from '@pikpak/dynamic-page'
import qs from 'qs'
import type {
  IConsumerEventClosePageParams,
  IFileInfo,
  TConsumerEventParams,
  THeaderOperates,
  WildcardHandler,
} from './consumer.type'
import { EConsumerEvents } from './consumer.type'
import ConsumerHeader from './ConsumerHeader.vue'

interface EmitEvent {
  (e: 'operate', type: THeaderOperates, file: IFileInfo, event: MouseEvent): void
}

const emits = defineEmits<EmitEvent>()

const route = useRoute()

const headerVisible = ref(false)
const fileInfo = reactive<IFileInfo>({
  id: '',
  fileExtension: '',
  name: '',
})
const consumerHeaderRef = ref<typeof ConsumerHeader>()
const consumer = ref<ReturnType<typeof createContentConsumer> | undefined>()
const showLoginDialog = inject<ShowLoginDialog>(InjectionKey.SHOW_LOGIN_DIALOG)

const hide = () => {
  headerVisible.value = false
  consumer.value?.close()
  window.scenePreviewMedia = false
}

const handleOperate = (type: THeaderOperates, e: MouseEvent) => {
  emits('operate', type, { ...fileInfo }, e)
}

const handleKeydown = (e: KeyboardEvent) => {
  consumer.value?.emitter.emit(EConsumerEvents.KEYDOWN, {
    key: e.key,
    shiftKey: e.shiftKey,
    ctrlKey: e.ctrlKey,
    metaKey: e.metaKey,
  })
}

const handleEmit = (event: string, params?: TConsumerEventParams) => {
  // console.log(event, params)
  switch (event) {
    case EConsumerEvents.CLOSE_PAGE:
      hide()
      consumer.value?.emitter.off('*', handleEmit as WildcardHandler)
      consumer.value = undefined
      window.removeEventListener('keydown', handleKeydown)
      // eslint-disable-next-line
      const p = params as IConsumerEventClosePageParams
      if (p && p.file === 'html' && p.scene === 'bookmark') {
        // html提取收藏成功之后跳转云盘收藏夹页面
        window.open(`https://${window.location.host}/drive/bookmarks`, '_blank')
      }
      break
    case EConsumerEvents.SHOW_HEADER:
      !headerVisible.value && (headerVisible.value = true)
      setTimeout(() => {
        consumerHeaderRef.value?.updateConfig(params)
      }, 0)
      break
    case EConsumerEvents.CHECK_LOGIN_STATUS:
      if (authByLocalStorage()) {
        // 已登录，让流程继续
        consumer.value?.emitter.emit(`${EConsumerEvents.CHECK_LOGIN_STATUS}:response`, true)
      } else {
        // 未登录，需要登录
        showLoginDialog?.({
          afterLogin() {
            consumer.value?.emitter.emit(`${EConsumerEvents.CHECK_LOGIN_STATUS}:response`, true)
          },
          afterClose() {
            if (!authByLocalStorage()) {
              // eslint-disable-next-line
              handleEmit(EConsumerEvents.CLOSE_PAGE)
            }
          },
        })
      }
      break
    default:
      break
  }
}

function handleClose() {
  // 通知消费页面进行关闭，方便进行埋点上报，消费页面需要实现关闭操作
  consumer.value?.emitter.emit(EConsumerEvents.CLOSE_PAGE, 'close')
  setTimeout(() => {
    handleEmit(EConsumerEvents.CLOSE_PAGE) // 兜底
  }, 300)
}

const handleHeaderHeightUpdate = (h: number) => {
  consumer.value?.emitter.setStyleProperty('--pp-consumer-header-height', `${h}px`)
}

const setFile = (data: IFileInfo, shareId: string, passCodeToken: string) => {
  if (consumer.value) {
    // 已经存在打开的页面，需要先关闭
    handleEmit(EConsumerEvents.CLOSE_PAGE)
  }

  const type = data.fileExtension?.replace(/^\./, '').toLowerCase()
  const params = qs.stringify({
    preview: true,
    scene: 'share',
    id: data.id,
    share_id: shareId,
    pass_code_token: passCodeToken,
    gcid: data.hash,
  })
  let url = data.url || `https://${window.location.host}/drive/app-extension/${type}/?${params}`
  url = `${url}&from=share_page`
  Object.assign(fileInfo, { ...data, type, url })
  consumer.value = createContentConsumer({ url }, { isShowLoading: true })
  consumer.value.emitter.on('*', handleEmit as WildcardHandler)
  consumer.value.open()
  window.history.pushState('media-preview', '', window.location.href)
  window.addEventListener('keydown', handleKeydown)
  window.scenePreviewMedia = true
}

watch(() => route.params, () => {
  consumer.value && handleEmit(EConsumerEvents.CLOSE_PAGE)
})

defineExpose({ setFile, hide })

onBeforeMount(() => {
  injectJsBridge()
})

onBeforeUnmount(() => {
  consumer.value && handleEmit(EConsumerEvents.CLOSE_PAGE)
})
</script>
<style lang="less">
.pp-consumer-iframe {
  height: 100% !important;
}
</style>
